<template>
  <Modal
    v-if="visible"
    id="proximity-search-modal"
    :visible="visible"
    :title="modalTitle"
    :size="'lg'"
    button1-class="primary"
    :button1-caption="capitalize($t('actions.create'))"
    :button1-disabled="saveButtonDisabled"
    :button2-visible="true"
    @confirmed="save"
    @cancelled="() => (visible = false)">
    <template #modal-after-form>
      <edit-geofence-group
        ref="editGeofenceGroup"
        :geofence-group-type="geofenceGroupType.PROXIMITY"
        :in-modal="true"
        :is-duplicate="true"
        :show-footer="false"
        :geofence-group-i-d="geofenceGroupID" />
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import { capitalize, computed, Ref, ref } from 'vue';

  import Modal from '@/common/components/controls/Modal.vue';
  import GeofenceGroupType from '@/common/models/geofenceGroups/GeofenceGroupType.enum';
  import EditGeofenceGroup from '@/org-app/components/geofenceGroups/EditGeofenceGroup.vue';
  import i18n from '@/vueI18n';

  // region Types
  interface IProximityGeofenceModalExposed {
    open(id: number | null): void;
  }

  interface IProximityGeofenceModalEmits {
    (e: 'proximityGeofenceCreated', value: number): void;
  }

  // endregion

  // region Emits
  const emit = defineEmits<IProximityGeofenceModalEmits>();
  // endregion

  // region Data
  const geofenceGroupType = GeofenceGroupType;
  const visible = ref(false);
  const editGeofenceGroup: Ref<InstanceType<typeof EditGeofenceGroup>> = ref(null);
  const geofenceGroupID = ref(null);
  // endregion

  // region Computed
  const saveButtonDisabled = computed(() => {
    if (editGeofenceGroup.value == null) {
      return false;
    }
    const { geofenceGroup } = editGeofenceGroup.value;
    const noName = geofenceGroup?.name == null || geofenceGroup?.name.trim() === '';
    const noGeofenceInList = geofenceGroup?.geofences == null || geofenceGroup?.geofences?.length === 0;
    return noName || noGeofenceInList;
  });
  const modalTitle = computed(() =>
    geofenceGroupID.value != null
      ? capitalize(i18n.global.t('actions.duplicateProximityGeofenceGroup'))
      : capitalize(i18n.global.t('actions.newProximityGeofenceGroup')),
  );
  // endregion

  // region Methods
  function open(id: number | null) {
    geofenceGroupID.value = id;
    visible.value = true;
  }

  function close() {
    visible.value = false;
  }

  async function save() {
    let createdGeofenceGroup = null;
    if (geofenceGroupID.value != null) {
      // Duplicate an existing geofence
      createdGeofenceGroup = await editGeofenceGroup.value.createCopyConfirmed();
    } else {
      // Create a new geofence
      createdGeofenceGroup = await editGeofenceGroup.value.saveChangesModalConfirmed();
    }

    if (createdGeofenceGroup) {
      emit('proximityGeofenceCreated', createdGeofenceGroup);
      close();
    }
  }

  // endregion

  // region Exposed
  defineExpose<IProximityGeofenceModalExposed>({
    open,
  });
  // endregion
</script>

<style lang="scss">
  @use '@/styles/variables' as *;

  #proximity-search-modal {
    .modal-dialog {
      max-height: fit-content;
    }

    .modal-content {
      height: 80vh;
    }

    .modal-body {
      .form-horizontal {
        padding: 0 !important;
      }

      #edit_geofenceGroup {
        height: 100% !important;
      }

      .general-section-container {
        flex-basis: 10rem !important;
      }
    }

    .map-and-panes-container {
      height: calc(100% - 10rem) !important;
    }

    .geofence-list-selection-modal {
      .modal-content {
        width: 78vw !important;
      }
    }
  }

  #proximity-search-modal {
    .map-and-panes-container {
      margin: 0 -1.6rem;
      #geofences-list-and-map {
        .pane-and-map {
          .map-panes {
            .map-panes-left {
              border: solid 0.1rem $borders-light;

              .map-pane {
                box-shadow: none;
                margin-top: 0;
              }
            }
          }

          .map-container {
            border: solid 0.1rem $borders-light;
          }
        }
      }
    }
  }
</style>
